import React from 'react';
import { Avatar, Badge, styled, Tooltip, type BadgeProps } from '@mui/material';

import useSettings from './hooks/useSettings';
import Icon from './Icon';

interface ImageAvatarProps {
	/** The module name */
	module: string;
	/** The item uid */
	uid: string;
	/** The item avatar */
	avatar?: string;
	/** The initials, used if the avatar file is not available */
	initials: string;
	/** The avatar dimensions */
	dimensions: number;
	/** The badge localized text */
	labelBadge?: string;
	/** The badge localized tooltip text */
	labelBadgeTooltip?: string;
	/** The badge color */
	badgeColor?: string;
	/** If true the field can be modified */
	isEdit?: boolean;
}

const StyledAvatar = styled(Avatar)<{ dimensions: number }>(({ dimensions, theme }) => ({
	width: dimensions,
	height: dimensions,
	fontSize: Math.floor((40 * dimensions) / 100),
	color: theme.palette.text.primary,
	backgroundColor: 'lightgrey',
	'& > .MuiAvatar-img': {
		objectFit: 'contain'
	}
}));

const StyledBadge = styled((props: BadgeProps & Pick<ImageAvatarProps, 'badgeColor'>) => {
	const { badgeColor, ...other } = props;
	return <Badge {...other} />;
})<{ badgeColor?: string }>(({ badgeColor, theme }) => ({
	'& > .MuiBadge-badge': {
		color: badgeColor ? theme.palette.getContrastText(badgeColor) : theme.palette.text.primary,
		backgroundColor: badgeColor || '#BDBDBD'
	}
}));

/** A component used to render the avatar of an item or it's intitials */
function ImageAvatar({
	module,
	uid,
	avatar,
	initials,
	dimensions,
	labelBadge,
	labelBadgeTooltip,
	badgeColor,
	isEdit
}: ImageAvatarProps) {
	const { settings } = useSettings(module);

	const [openTooltip, setOpenTooltip] = React.useState(false);

	const url = avatar ? `${import.meta.env.VITE_ENDPOINT}/resources/${module}/${uid}/${avatar}?v=${Date.now()}` : '';

	const avatarComponent = (
		<StyledAvatar dimensions={dimensions} src={url}>
			{initials ? initials : <Icon icon={settings?.modu_icon || 'person'} />}
		</StyledAvatar>
	);

	return isEdit ? (
		avatarComponent
	) : (
		<StyledBadge
			overlap="circular"
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right'
			}}
			badgeContent={labelBadge}
			badgeColor={badgeColor}
			invisible={!labelBadge}
		>
			<Tooltip
				title={labelBadgeTooltip || ''}
				open={!!labelBadge && openTooltip}
				onOpen={() => setOpenTooltip(true)}
				onClose={() => setOpenTooltip(false)}
				arrow
			>
				<div>{avatarComponent}</div>
			</Tooltip>
		</StyledBadge>
	);
}

export default React.memo(ImageAvatar);
